import { useMemo } from 'react';
import {
  AssignmentRequestModel,
  AssignmentViewModel,
  GetBookedAssignmentsForUserAndDateList,
  StayOverTypeEnum,
} from 'api/model';
import { IAddress } from 'utils/address';
import useUsers from 'contexts/basicData/useUsers';
import useCompanies from 'contexts/basicData/useCompanies';
import { makeDateComparator } from 'utils/sorting';
import useZipCodeAreas from 'hooks/useZipCodeAreas';

export interface DayDrivingTime {
  type: 'uppdrag' | 'annonsförfrågan';
  start: Date;
  end: Date;
  breakTimeMinutes: number;
  fromAddress: IAddress;
  toAddress: IAddress;
  assignmentRequest?: AssignmentRequestModel;
  assignment?: AssignmentViewModel;
  timeFromPreviousEnd?: number;
  timeToNextStart?: number;
  startsAwayFromPreviousLocation?: boolean;
  endsAwayFromNextLocation?: boolean;
  stayoverType?: StayOverTypeEnum;
  hasEnoughBreakTimeUntilAssignmentEnd?: boolean;
  hasTooMuchBreakTimeUntilAssignmentEnd?: boolean;
  minRequiredBreakMinutesForAssignment?: number;
  minRequiredCumulativeBreakMinutes?: number;
}

interface DayDrivingTimesProps {
  bookedAssignments?: GetBookedAssignmentsForUserAndDateList | undefined;
  assignmentRequests?: AssignmentRequestModel[] | undefined;
  userId: number;
}

const useDayDrivingTimes = ({
  bookedAssignments,
  assignmentRequests = [],
  userId,
}: DayDrivingTimesProps) => {
  const { companies } = useCompanies();
  const users = useUsers();
  const { zipCodeAreaFromZipCode } = useZipCodeAreas();

  const firstLogisticsCompany = useMemo(
    () =>
      companies.find(
        (c) => c.isLogisticsCenter && c.name === 'Logistik - Strängnäs'
      ),
    [companies]
  );

  const dayDrivingTimes: DayDrivingTime[] = useMemo(() => {
    const result: DayDrivingTime[] = [];

    // Get backend-provided driving info dictionaries
    const drivingInfo = bookedAssignments?.drivingInfo ?? {};
    const assignmentRequestDrivingInfo =
      bookedAssignments?.assignmentRequestDrivingInfo ?? {};

    // loop through booked assignments and add to result
    if (users[userId]) {
      bookedAssignments?.assignments.forEach((assignment) => {
        const { estimatedDuration, endTime, startTime, estimatedStartTime } =
          assignment.assignment;
        const usedStart = startTime ?? estimatedStartTime;
        if (usedStart && estimatedDuration) {
          const estimatedLoadingTime =
            assignment.assignment.estimatedLoadingTime ?? 0;
          const estimatedUnloadingTime =
            assignment.assignment.estimatedUnloadingTime ?? 0;
          // Elbilars laddbuffert är belagd (icke-körande) tid precis som lossning —
          // samma summa som backendens härledda sluttid, annars slutar elbilsuppdrag
          // 30–60 min för tidigt i dagsöversikten jämfört med tidslinjen/körtidscachen.
          const electricCarBufferTime =
            assignment.assignment.electricCarBufferTime ?? 0;
          const estimatedWorkTime =
            estimatedDuration +
            estimatedLoadingTime +
            estimatedUnloadingTime +
            electricCarBufferTime;
          const usedEnd =
            endTime ?? new Date(usedStart.getTime() + estimatedWorkTime);

          // Get backend-computed driving info for this assignment
          const backendInfo = drivingInfo[assignment.assignment.assignmentID];

          result.push({
            type: 'uppdrag',
            start: usedStart,
            end: usedEnd,
            breakTimeMinutes: assignment.assignment.breakTime,
            fromAddress: {
              name: assignment.assignment.fromName,
              address: assignment.assignment.fromAddress,
              city: assignment.assignment.fromCity,
              zip: assignment.assignment.fromZip,
              county: assignment.assignment.fromCounty,
            },
            toAddress: {
              name: assignment.assignment.toName,
              address: assignment.assignment.toAddress,
              city: assignment.assignment.toCity,
              zip: assignment.assignment.toZip,
              county: assignment.assignment.toCounty,
            },
            assignment,
            stayoverType: assignment.assignment.stayOverTypeID,
            // Use backend-provided values
            startsAwayFromPreviousLocation:
              backendInfo?.startsAwayFromPreviousLocation,
            endsAwayFromNextLocation: backendInfo?.endsAwayFromNextLocation,
            hasEnoughBreakTimeUntilAssignmentEnd:
              backendInfo?.hasEnoughBreakTimeUntilAssignmentEnd,
            hasTooMuchBreakTimeUntilAssignmentEnd:
              backendInfo?.hasTooMuchBreakTimeUntilAssignmentEnd,
            minRequiredBreakMinutesForAssignment:
              backendInfo?.minRequiredBreakMinutesForAssignment,
            minRequiredCumulativeBreakMinutes:
              backendInfo?.minRequiredCumulativeBreakMinutes,
            timeFromPreviousEnd:
              backendInfo?.timeFromPreviousEndMs ?? undefined,
            timeToNextStart: backendInfo?.timeToNextStartMs ?? undefined,
          });
        }
      });
    }

    // loop through assignment requests and splice into result
    assignmentRequests
      .filter(
        (x) =>
          !bookedAssignments?.assignments.some((y) =>
            x.allocatedAssignments.some(
              (z) => z.assignmentID === y.assignment.assignmentID
            )
          )
      )
      .forEach((assignmentRequest) => {
        const fromCompany = companies.find(
          (c) => c.companyID === assignmentRequest.fromCompanyID
        );
        const toCompany = companies.find(
          (c) => c.companyID === assignmentRequest.toCompanyID
        );

        const derivedDuration =
          firstLogisticsCompany?.pricesFromHere.find(
            (price) => price.toCompanyId === assignmentRequest.toCompanyID
          )?.fixedTimeInMilliseconds ?? 0;

        const derivedEndTime =
          new Date(
            assignmentRequest.targetStartTime.getTime() + derivedDuration
          ) ?? undefined;

        // Get backend-computed driving info for this assignment request
        const backendInfo =
          assignmentRequestDrivingInfo[assignmentRequest.requestID];

        result.push({
          type: 'annonsförfrågan',
          start: assignmentRequest.targetStartTime,
          end: derivedEndTime,
          breakTimeMinutes: 0,
          fromAddress: {
            name: fromCompany?.name ?? '-',
            address: fromCompany?.address ?? '-',
            city: fromCompany?.city ?? '-',
            zip: fromCompany?.zip ?? '-',
            // Företaget saknar län — härled det ur postnumret som adressbokens
            // företagsval gör, annars ärver utfyllnaden intill förfrågan inget län.
            county: fromCompany
              ? zipCodeAreaFromZipCode(fromCompany.zip)
              : undefined,
          },
          toAddress: {
            name: toCompany?.name ?? '-',
            address: toCompany?.address ?? '-',
            city: toCompany?.city ?? '-',
            zip: toCompany?.zip ?? '-',
            county: toCompany
              ? zipCodeAreaFromZipCode(toCompany.zip)
              : undefined,
          },
          assignmentRequest,
          // Use backend-provided values
          startsAwayFromPreviousLocation:
            backendInfo?.startsAwayFromPreviousLocation,
          endsAwayFromNextLocation: backendInfo?.endsAwayFromNextLocation,
          hasEnoughBreakTimeUntilAssignmentEnd:
            backendInfo?.hasEnoughBreakTimeUntilAssignmentEnd,
          hasTooMuchBreakTimeUntilAssignmentEnd:
            backendInfo?.hasTooMuchBreakTimeUntilAssignmentEnd,
          timeFromPreviousEnd: backendInfo?.timeFromPreviousEndMs ?? undefined,
          timeToNextStart: backendInfo?.timeToNextStartMs ?? undefined,
        });
      });

    // sort the result by start time
    result.sort(makeDateComparator((a) => a.start));

    return result;
  }, [
    users,
    userId,
    assignmentRequests,
    bookedAssignments?.assignments,
    bookedAssignments?.drivingInfo,
    bookedAssignments?.assignmentRequestDrivingInfo,
    companies,
    firstLogisticsCompany?.pricesFromHere,
    zipCodeAreaFromZipCode,
  ]);

  return dayDrivingTimes;
};

export default useDayDrivingTimes;
